import { CommonModule } from "@angular/common";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { firstValueFrom } from "rxjs";
import { getEnv } from "./environment";
import {
  InsightsSnapshot,
  InsightsStatus,
  LogReplay,
  PublicEvent,
  PublicEventPage,
  PublicIntendedUse,
  PublicSnapshotPage,
  PublicWrp,
  RULES_VERSION,
  compareSequence,
  displayNames,
  replay,
} from "./transparency-log";
import { IntendedUse, deriveIntendedUses } from "./requested-data";
import { WatchFinding, evaluateFindings } from "./watch-rules";
import { TrustAnchor, loadTrustAnchor } from "./signature-verification";
import {
  LogConsistencyProof,
  LogTreeHead,
  TreeHeadCheck,
  TreeHeadConsistency,
  leafHash,
  rootHash,
  toHex,
  verifyConsistencyProof,
} from "./merkle";
import {
  WatchContext,
  WatchView,
  WrpRow,
  buildWrpRows,
  certificatesByEvent,
  registrationsByEvent,
  viewLink,
} from "./watch-view-model";
import { ActivityViewComponent } from "./views/activity-view.component";
import {
  FindingsViewComponent,
  SeverityFilter,
} from "./views/findings-view.component";
import { RegisterViewComponent } from "./views/register-view.component";
import {
  IntendedUseCheck,
  RequestsViewComponent,
} from "./views/requests-view.component";

const AUTO_REFRESH_MS = 60_000;
const AUTO_REFRESH_KEY = "transparency-watch.auto-refresh";
const VIEWS: WatchView[] = ["findings", "register", "requests", "activity"];

type RegisterCheck =
  | { status: "consistent" | "inconsistent"; asOfSequence: string }
  | { status: "unavailable"; reason: string };

/** Fields of the registrar's discovery document the monitor follows. */
interface RegistrarDiscovery {
  certificate_authority_uri?: string;
  certificate_event_log_tree_head_uri?: string;
  register_snapshot_uri?: string;
}

@Component({
  selector: "watch-root",
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    FindingsViewComponent,
    RegisterViewComponent,
    RequestsViewComponent,
    ActivityViewComponent,
  ],
  templateUrl: "./ecosystem-monitor.component.html",
  styleUrl: "./ecosystem-monitor.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EcosystemMonitorComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  readonly api = getEnv().api;
  readonly apiHost = hostOf(this.api);
  readonly views: Array<{ id: WatchView; label: string; icon: string }> = [
    { id: "findings", label: "Findings", icon: "policy" },
    { id: "register", label: "Register", icon: "badge" },
    { id: "requests", label: "Requested data", icon: "manage_search" },
    { id: "activity", label: "Activity", icon: "history" },
  ];

  events: PublicEvent[] = [];
  findings: WatchFinding[] = [];
  names = new Map<string, string>();
  wrpRows: WrpRow[] = [];
  intendedUses: IntendedUse[] = [];
  /** Attestation types requested by current intended uses. */
  requestedTypes = 0;
  context?: WatchContext;
  view: WatchView = "findings";
  severityFilter: SeverityFilter = "all";
  selectedFindingId?: string;
  selectedWrpId?: string;
  selectedType?: string;
  autoRefresh = false;
  registerCheck?: RegisterCheck;
  intendedUseCheck?: IntendedUseCheck;
  signatureCheck?:
    | { status: "verified" | "failed"; checked: number; failed: number }
    | { status: "unavailable"; reason: string };
  treeCheck?: TreeHeadCheck;
  private trustAnchor?: TrustAnchor;
  private discovery?: RegistrarDiscovery;
  /** log_epoch of the events held; another epoch renumbers the log. */
  private logEpoch?: number;
  /** Merkle leaf hashes of the events held, in the same order. */
  private leafHashes: Array<Uint8Array<ArrayBuffer>> = [];
  /** The last tree head whose root matched the replay; R-13 starts there. */
  private verifiedTreeHead?: { size: number; rootHash: string };
  loading = true;
  refreshing = false;
  error = "";
  throughSequence = "0";
  newEvents = 0;
  lastUpdated?: string;
  validCertificates = 0;

  get highCount(): number {
    return this.countBySeverity("high");
  }

  get warningCount(): number {
    return this.countBySeverity("warning");
  }

  /** Events that are not evidence of a high or warning finding. */
  get normalCount(): number {
    const flagged = new Set(
      this.findings
        .filter((finding) => finding.severity !== "info")
        .flatMap((finding) => finding.evidence.map((event) => event.event_id)),
    );
    return this.events.filter((event) => !flagged.has(event.event_id)).length;
  }

  ngOnInit(): void {
    this.applyRoute();
    const onHashChange = () => {
      this.applyRoute();
      this.changeDetector.markForCheck();
    };
    window.addEventListener("hashchange", onHashChange);

    this.autoRefresh = readSetting(AUTO_REFRESH_KEY) === "on";
    const timer = window.setInterval(() => {
      if (this.autoRefresh && !this.refreshing && !document.hidden) {
        void this.refresh();
      }
    }, AUTO_REFRESH_MS);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener("hashchange", onHashChange);
      window.clearInterval(timer);
    });
    void this.refresh();
  }

  /** Shows a view; the URL fragment records it with the selection. */
  show(view: WatchView, key?: string): void {
    this.view = view;
    const hash = viewLink(view, key);
    if (location.hash !== hash) {
      history.replaceState(null, "", hash);
    }
  }

  showFindings(severity: SeverityFilter): void {
    this.severityFilter = severity;
    this.show("findings");
  }

  openWrp(wrpId: string): void {
    this.selectedWrpId = wrpId;
    this.show("register", wrpId);
  }

  openFinding(findingId: string): void {
    this.severityFilter = "all";
    this.selectedFindingId = findingId;
    this.show("findings", findingId);
  }

  selectWrp(wrpId?: string): void {
    this.selectedWrpId = wrpId;
    this.show("register", wrpId);
  }

  selectType(type?: string): void {
    this.selectedType = type;
    this.show("requests", type);
  }

  selectFinding(findingId?: string): void {
    this.selectedFindingId = findingId;
    this.show("findings", findingId);
  }

  /** Reads view and selection from the fragment, e.g. #requests/<vct>. */
  private applyRoute(): void {
    const [view, ...rest] = location.hash.replace(/^#/, "").split("/");
    const key = rest.length ? safeDecode(rest.join("/")) : undefined;
    if (!VIEWS.includes(view as WatchView)) {
      this.view = "findings";
      return;
    }
    this.view = view as WatchView;
    if (key === undefined) return;
    if (this.view === "register") this.selectedWrpId = key;
    if (this.view === "requests") this.selectedType = key;
    if (this.view === "findings") {
      this.severityFilter = "all";
      this.selectedFindingId = key;
    }
  }

  toggleAutoRefresh(): void {
    this.autoRefresh = !this.autoRefresh;
    writeSetting(AUTO_REFRESH_KEY, this.autoRefresh ? "on" : "off");
  }

  /**
   * Synchronizes incrementally: only events after the last known sequence
   * are fetched, pinned to one through_sequence so the pages form a snapshot.
   * The Insights API projects the log and may lag behind it, so the register
   * is read at the latest sequence it serves (at most through_sequence) and
   * compared with the log replayed up to that sequence.
   */
  async refresh(): Promise<void> {
    this.refreshing = true;
    this.error = "";
    this.changeDetector.markForCheck();
    try {
      let page = await this.readEventsAfter(
        this.throughSequence,
        this.logEpoch,
      );
      if (page.status === "epoch_changed") {
        // Sequence numbers changed meaning: discard the copy and start over.
        this.events = [];
        this.throughSequence = "0";
        this.logEpoch = undefined;
        this.leafHashes = [];
        this.verifiedTreeHead = undefined;
        page = await this.readEventsAfter(this.throughSequence);
        if (page.status === "epoch_changed") {
          throw new Error(
            "The log epoch changed while the log was read. Try again later.",
          );
        }
      }
      this.events = [...this.events, ...page.events];
      this.newEvents = page.events.length;
      this.throughSequence = page.throughSequence;
      this.logEpoch = page.logEpoch;

      const anchor = await this.readTrustAnchor();
      const replayed = await replay(
        this.events,
        anchor.status === "ok" ? anchor.value : undefined,
      );
      const signed = [
        ...replayed.certificates.values(),
        ...[...replayed.registrations.values()].flat(),
      ];
      const failed = signed.filter((item) => item.signature !== "valid").length;
      this.signatureCheck =
        anchor.status === "ok"
          ? {
              status: failed ? "failed" : "verified",
              checked: signed.length,
              failed,
            }
          : { status: "unavailable", reason: anchor.reason };
      this.treeCheck = await this.checkTreeHead();
      const { asOf, evaluatedAt } = await this.readComparePosition();
      const [register, remoteUses] = await Promise.all([
        this.readSnapshot<PublicWrp>("wrps", asOf, evaluatedAt),
        this.readSnapshot<PublicIntendedUse>(
          "intended-uses",
          asOf,
          evaluatedAt,
        ),
      ]);
      // A register behind the log is compared with the log up to its
      // sequence. Signatures play no part in that, so no trust anchor.
      const comparedReplay =
        compareSequence(asOf, this.throughSequence) < 0 &&
        (register.status === "ok" || remoteUses.status === "ok")
          ? await replay(
              this.events.filter(
                (event) => compareSequence(event.sequence_number, asOf) <= 0,
              ),
            )
          : undefined;
      this.intendedUses = deriveIntendedUses(replayed);
      this.findings = evaluateFindings(
        replayed,
        this.intendedUses,
        {
          wrps: register.status === "ok" ? register.data : undefined,
          intendedUses:
            remoteUses.status === "ok" ? remoteUses.data : undefined,
          replayed: comparedReplay,
        },
        this.treeCheck,
      );
      this.intendedUseCheck =
        remoteUses.status === "ok"
          ? {
              status: this.findings.some((finding) =>
                finding.id.startsWith("R-06:intended-uses:"),
              )
                ? "inconsistent"
                : "consistent",
              asOfSequence: asOf,
            }
          : { status: "unavailable", reason: remoteUses.reason };
      this.registerCheck =
        register.status === "ok"
          ? {
              status: this.findings.some((finding) => finding.rule === "R-06")
                ? "inconsistent"
                : "consistent",
              asOfSequence: asOf,
            }
          : { status: "unavailable", reason: register.reason };

      this.names = this.resolveNames(replayed, register);
      this.validCertificates = [...replayed.certificates.values()].filter(
        (record) => !record.revoked,
      ).length;
      this.wrpRows = buildWrpRows(replayed, this.findings, this.names);
      this.requestedTypes = new Set(
        this.intendedUses
          .filter((use) => use.current)
          .flatMap((use) =>
            use.credentials.flatMap((credential) =>
              credential.types.length ? credential.types : [""],
            ),
          ),
      ).size;
      this.context = {
        api: this.api,
        asOfSequence: asOf,
        evaluatedAt:
          register.status === "ok"
            ? register.snapshot.evaluated_at
            : remoteUses.status === "ok"
              ? remoteUses.snapshot.evaluated_at
              : undefined,
        names: this.names,
        certificates: certificatesByEvent(replayed),
        records: replayed.certificates,
        registrations: registrationsByEvent(replayed),
      };
      this.lastUpdated = new Date().toISOString();
    } catch (error) {
      this.error = describeError(error);
    } finally {
      this.loading = false;
      this.refreshing = false;
      this.changeDetector.markForCheck();
    }
  }

  private countBySeverity(severity: WatchFinding["severity"]): number {
    return this.findings.filter((finding) => finding.severity === severity)
      .length;
  }

  /**
   * Reads the events after a sequence. Stops as soon as a page reports
   * another log_epoch than `epoch` (that of the events held) or than the
   * pages before it.
   */
  private async readEventsAfter(
    afterSequence: string,
    epoch?: number,
  ): Promise<
    | {
        status: "ok";
        events: PublicEvent[];
        throughSequence: string;
        logEpoch?: number;
      }
    | { status: "epoch_changed" }
  > {
    const events: PublicEvent[] = [];
    let after: string | undefined = afterSequence;
    let through: string | undefined;
    let logEpoch = epoch;
    while (after !== undefined) {
      const query = new URLSearchParams({
        limit: "500",
        after_sequence: after,
      });
      if (through) query.set("through_sequence", through);
      const page: PublicEventPage = await firstValueFrom(
        this.http.get<PublicEventPage>(
          `${this.api}/certificate-events?${query}`,
        ),
      );
      if (logEpoch !== undefined && page.log_epoch !== logEpoch) {
        return { status: "epoch_changed" };
      }
      logEpoch = page.log_epoch;
      events.push(...page.data);
      through ??= page.through_sequence;
      after = page.pagination.next_after_sequence ?? undefined;
    }
    return {
      status: "ok",
      events,
      throughSequence: through ?? afterSequence,
      logEpoch,
    };
  }

  /** The registrar's discovery document, cached for the session once read. */
  private async readDiscovery(): Promise<RegistrarDiscovery> {
    return (this.discovery ??= await firstValueFrom(
      this.http.get<RegistrarDiscovery>(
        `${this.api}/.well-known/registrar-api`,
      ),
    ));
  }

  /**
   * Reads the tree head for the replayed log size from
   * certificate_event_log_tree_head_uri and compares its root with the
   * Merkle tree over the replayed events. If a tree head was verified at an
   * earlier synchronisation, a consistency proof must show that the new tree
   * extends it. Leaf hashes are kept, as events do not change within an
   * epoch.
   */
  private async checkTreeHead(): Promise<TreeHeadCheck> {
    try {
      const uri = (await this.readDiscovery())
        .certificate_event_log_tree_head_uri;
      if (!uri) {
        return {
          status: "unavailable",
          reason: "The discovery document names no tree head.",
        };
      }
      const treeSize = this.throughSequence;
      const link = `${uri}?${new URLSearchParams({ tree_size: treeSize })}`;
      const head = await firstValueFrom(this.http.get<LogTreeHead>(link));
      if (
        typeof head?.tree_size !== "string" ||
        typeof head.root_hash !== "string"
      ) {
        return { status: "unavailable", reason: "The tree head is malformed." };
      }
      if (head.log_epoch !== this.logEpoch) {
        return {
          status: "unavailable",
          reason: `The tree head describes epoch ${head.log_epoch} of the log, the replayed log is epoch ${this.logEpoch}.`,
        };
      }
      if (head.hash_algorithm !== "sha-256") {
        return {
          status: "unavailable",
          reason: `The tree head uses ${head.hash_algorithm}; the monitor computes sha-256.`,
        };
      }

      this.leafHashes.push(
        ...(await Promise.all(
          this.events.slice(this.leafHashes.length).map(leafHash),
        )),
      );
      const computedRoot = toHex(await rootHash(this.leafHashes));
      const matches =
        head.tree_size === treeSize &&
        head.root_hash.toLowerCase() === computedRoot;

      const size = Number(treeSize);
      const earlier = this.verifiedTreeHead;
      let consistency: TreeHeadConsistency | undefined;
      if (earlier && earlier.size > 0 && earlier.size < size) {
        const proofUri = new URL(
          "proofs/consistency",
          new URL(uri, location.href),
        );
        proofUri.search = new URLSearchParams({
          first_tree_size: String(earlier.size),
          second_tree_size: treeSize,
        }).toString();
        consistency = { fromSize: String(earlier.size), link: `${proofUri}` };
        try {
          const proof = await firstValueFrom(
            this.http.get<LogConsistencyProof>(consistency.link),
          );
          consistency.valid = await verifyConsistencyProof(
            earlier.size,
            size,
            earlier.rootHash,
            head.root_hash,
            proof,
          );
        } catch (error) {
          // The root is still compared; the proof is read at the next sync.
          consistency.error = describeError(error);
        }
      }
      if (matches && !consistency?.error) {
        this.verifiedTreeHead = { size, rootHash: computedRoot };
      }
      return {
        status: matches && consistency?.valid !== false ? "verified" : "failed",
        treeSize,
        head,
        computedRoot,
        link,
        checkedAt: new Date().toISOString(),
        consistency,
      };
    } catch (error) {
      return { status: "unavailable", reason: describeError(error) };
    }
  }

  /**
   * The log position to compare the register at: the latest sequence the
   * Insights API serves (register_snapshot_uri of the discovery document),
   * at most the end of the replayed log. At that latest state the listings
   * are pinned to its evaluated_at, so /wrps and /intended-uses describe the
   * same state even if the projection advances meanwhile.
   */
  private async readComparePosition(): Promise<{
    asOf: string;
    evaluatedAt?: string;
  }> {
    try {
      const discovery = await this.readDiscovery().catch(() => undefined);
      const { snapshot } = await firstValueFrom(
        this.http.get<InsightsStatus>(
          discovery?.register_snapshot_uri ?? `${this.api}/snapshot`,
        ),
      );
      if (compareSequence(snapshot.as_of_sequence, this.throughSequence) <= 0) {
        return {
          asOf: snapshot.as_of_sequence,
          evaluatedAt: snapshot.evaluated_at,
        };
      }
    } catch {
      // Without it the register is read at the end of the log; a projection
      // that lags behind then answers 409, which the check reports.
    }
    return { asOf: this.throughSequence };
  }

  /**
   * The trust anchor (the registrar's root CA) is found through the discovery
   * document (certificate_authority_uri) and cached for the session.
   */
  private async readTrustAnchor(): Promise<
    { status: "ok"; value: TrustAnchor } | { status: "error"; reason: string }
  > {
    if (this.trustAnchor) {
      return { status: "ok", value: this.trustAnchor };
    }
    try {
      const discovery = await this.readDiscovery();
      if (!discovery.certificate_authority_uri) {
        return {
          status: "error",
          reason: "The discovery document names no certificate authority.",
        };
      }
      const pem = await firstValueFrom(
        this.http.get(discovery.certificate_authority_uri, {
          responseType: "text",
        }),
      );
      this.trustAnchor = {
        ...(await loadTrustAnchor(pem)),
        // Certificates name their sub CA by caIssuers.
        fetchCertificate: async (url) =>
          new Uint8Array(
            await firstValueFrom(
              this.http.get(url, { responseType: "arraybuffer" }),
            ),
          ),
      };
      return { status: "ok", value: this.trustAnchor };
    } catch (error) {
      return { status: "error", reason: describeError(error) };
    }
  }

  /**
   * Reads every page of an Insights endpoint at one log position. The cursor
   * fixes the state, so every page must report the snapshot of the first.
   */
  private async readSnapshot<T>(
    path: string,
    asOfSequence: string,
    evaluatedAt?: string,
  ): Promise<
    | { status: "ok"; data: T[]; snapshot: InsightsSnapshot }
    | { status: "error"; reason: string }
  > {
    const data: T[] = [];
    let snapshot: InsightsSnapshot | undefined;
    let cursor: string | null = null;
    try {
      do {
        // A cursor is only accepted together with the first request's query.
        const query = new URLSearchParams({
          limit: "500",
          as_of_sequence: asOfSequence,
        });
        if (evaluatedAt) query.set("evaluated_at", evaluatedAt);
        if (cursor) query.set("cursor", cursor);
        const page: PublicSnapshotPage<T> = await firstValueFrom(
          this.http.get<PublicSnapshotPage<T>>(`${this.api}/${path}?${query}`),
        );
        if (!snapshot) {
          if (page.snapshot?.as_of_sequence !== asOfSequence) {
            return {
              status: "error",
              reason: `/${path} does not report a matching snapshot.`,
            };
          }
          if (page.snapshot.log_epoch !== this.logEpoch) {
            return {
              status: "error",
              reason: `/${path} describes epoch ${page.snapshot.log_epoch} of the log, the replayed log is epoch ${this.logEpoch}.`,
            };
          }
          // Other derivation rules give another register from the same log.
          if (page.snapshot.rules_version !== RULES_VERSION) {
            return {
              status: "error",
              reason: `/${path} applies derivation rules version ${page.snapshot.rules_version}, the monitor version ${RULES_VERSION}.`,
            };
          }
          snapshot = page.snapshot;
        } else if (!sameSnapshot(page.snapshot, snapshot)) {
          return {
            status: "error",
            reason: `/${path} reports another snapshot on a later page, so its pages do not describe one state.`,
          };
        }
        data.push(...page.data);
        cursor = page.pagination.next_cursor;
      } while (cursor);
      return { status: "ok", data, snapshot };
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 409) {
        return {
          status: "error",
          reason: `The Insights API has not projected sequence ${asOfSequence} yet.`,
        };
      }
      return { status: "error", reason: describeError(error) };
    }
  }

  private resolveNames(
    replayed: LogReplay,
    register: { status: "ok"; data: PublicWrp[] } | { status: "error" },
  ): Map<string, string> {
    const names = new Map(
      register.status === "ok"
        ? register.data.map((wrp) => [wrp.wrp_id, wrp.display_name])
        : [],
    );
    // Names read from the certificates win over the register's claims.
    displayNames(replayed).forEach((name, wrpId) => names.set(wrpId, name));
    return names;
  }
}

function describeError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    return error.status === 0
      ? `The transparency API at ${error.url ?? "the configured URL"} could not be reached.`
      : `The transparency API answered ${error.status} ${error.statusText}.`;
  }
  return error instanceof Error
    ? error.message
    : "The public transparency API could not be reached.";
}

/** Both name the same register state of the same log. */
function sameSnapshot(
  left: InsightsSnapshot | undefined,
  right: InsightsSnapshot,
): boolean {
  return (
    left?.log_id === right.log_id &&
    left.log_epoch === right.log_epoch &&
    left.as_of_sequence === right.as_of_sequence &&
    left.evaluated_at === right.evaluated_at &&
    left.rules_version === right.rules_version
  );
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

function readSetting(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSetting(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Settings are a convenience; ignore blocked storage.
  }
}
